'use client';
import { cn } from '@ahm/common-helpers';
import { useState, useEffect } from 'react';

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fill?: boolean;
  blurDataURL?: string;
  placeholder?: 'blur' | 'empty';
  priority?: boolean;
  width?: number | string;
  height?: number | string;
  onError?: () => void;
  quality?: number; // For next/image compatibility, but not used in standard img tag
}

export const Image = ({
  src,
  alt,
  className,
  fill = false,
  blurDataURL,
  placeholder = 'empty',
  priority = false, // Trong React SPA, priority chỉ đơn giản là loading="eager"
  style,
  sizes,
  width,
  height,
  onError,
  quality, // Accepted for compatibility but not used (standard img doesn't support quality)
  ...props
}: ImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);

  // Nếu đã có ảnh trong cache trình duyệt, set loaded luôn để tránh flash
  useEffect(() => {
    if (!src) return;
    const img = new window.Image();
    img.src = src;
    if (img.complete) {
      setIsLoaded(true);
    }
  }, [src]);

  // Xử lý style cho trường hợp fill
  const fillClass = fill ? 'absolute inset-0 h-full w-full' : '';

  // Xử lý style cho width/height khi không dùng fill
  const dimensionStyle =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    !fill && (width || height)
      ? {
          width: typeof width === 'number' ? `${width}px` : width,
          height: typeof height === 'number' ? `${height}px` : height,
          ...style,
        }
      : style;

  // Base classes cho ảnh thật
  const baseImageClass = cn(
    'transition-all duration-500 ease-in-out', // Hiệu ứng fade
    fillClass,
    // Nếu chưa load xong và có placeholder blur -> ẩn ảnh thật đi hoặc mờ đi
    // Nếu đã load xong -> hiện rõ
    isLoaded ? 'opacity-100 blur-0 scale-100' : 'opacity-0 blur-xl scale-95',
    className
  );

  return (
    <div
      className={cn(
        'overflow-hidden',
        fill ? 'absolute inset-0 h-full w-full' : 'relative'
        // Nếu dùng fill, parent container bên ngoài cần có class 'relative'
      )}
    >
      {/* 1. Lớp Blur Placeholder (Hiển thị khi ảnh chính chưa load) */}
      {placeholder === 'blur' && blurDataURL && !isLoaded && (
        <img
          src={blurDataURL}
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          alt={alt || 'placeholder'}
          className={cn(
            fillClass,
            'object-cover filter scale-105 brightness-30', // scale-105 để che viền trắng khi blur
            'transition-opacity duration-500',
            isLoaded ? 'opacity-0' : 'opacity-100' // Ẩn dần khi ảnh thật hiện lên
          )}
          aria-hidden="true"
        />
      )}

      {/* 2. Ảnh chính */}
      <img
        src={src}
        alt={alt}
        loading={priority ? 'eager' : 'lazy'} // Thay thế logic priority của Next.js
        decoding={priority ? 'sync' : 'async'}
        fetchPriority={priority ? 'high' : 'auto'}
        onLoad={() => setIsLoaded(true)}
        onError={onError}
        className={baseImageClass}
        style={dimensionStyle}
        sizes={sizes}
        {...props}
      />
    </div>
  );
};
